import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Board/ElementDescriptionPopup.less';
import '@/styles/Board/TiptapTextEditor.less';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Modal from 'react-modal';
import { MemoizedEditorCore } from '@/components/Board/TextEditor/InlineTextEditor';
import TiptapFloatingToolbar from '@/components/Board/TextEditor/TiptapFloatingToolbar';
/**
 * Константы для позиционирования попапа
 */
const POPUP_CONSTANTS = {
    WIDTH: 400,
    HEIGHT: 300,
    MARGIN: 0,
    MIN_POSITION: 10,
};
/**
 * Вычисляет оптимальную позицию попапа относительно элемента
 *
 * @param elementX - X координата левого верхнего угла элемента на экране
 * @param elementY - Y координата левого верхнего угла элемента на экране
 * @returns Позиция попапа { x, y }
 */
const calculatePopupPosition = (elementX, elementY) => {
    const { WIDTH, HEIGHT, MARGIN, MIN_POSITION } = POPUP_CONSTANTS;
    let popupX;
    let popupY;
    // Проверяем, есть ли место слева от левого верхнего угла элемента
    const leftSpace = elementX;
    if (leftSpace >= WIDTH + MARGIN) {
        // Размещаем попап слева от угла элемента
        popupX = elementX - WIDTH - MARGIN;
        popupY = elementY;
    }
    else {
        // Не помещается слева - позиционируем справа от угла элемента
        const rightSpace = window.innerWidth - elementX;
        if (rightSpace >= WIDTH + MARGIN) {
            popupX = elementX + MARGIN;
            popupY = elementY;
        }
        else {
            // Не помещается ни слева, ни справа - центрируем по горизонтали
            popupX = Math.max(MIN_POSITION, (window.innerWidth - WIDTH) / 2);
            popupY = elementY;
        }
    }
    // Корректируем по вертикали, чтобы попап не выходил за границы экрана
    if (popupY + HEIGHT > window.innerHeight) {
        popupY = Math.max(MIN_POSITION, window.innerHeight - HEIGHT - MARGIN);
    }
    if (popupY < MIN_POSITION) {
        popupY = MIN_POSITION;
    }
    return { x: popupX, y: popupY };
};
/**
 * Попап для отображения и редактирования описания элемента доски
 *
 * @remarks
 * Компонент предоставляет:
 * - Просмотр описания в формате Markdown
 * - Редактирование описания через Tiptap редактор
 * - Автоматическое позиционирование относительно элемента
 * - Сохранение при клике вне попапа
 */
const ElementDescriptionPopup = ({ isOpen, element, stagePosition, zoom, onClose, onEdit, }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editorReady, setEditorReady] = useState(false);
    const popupRef = useRef(null);
    const editorRef = useRef(null);
    /**
     * Обработчик изменения содержимого редактора
     */
    const handleMarkdownChange = useCallback((markdown) => {
        setEditContent(markdown);
    }, []);
    /**
     * Обработчик сохранения описания
     */
    const handleSave = useCallback(() => {
        var _a;
        if (!element)
            return;
        const markdown = ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getMarkdown()) || editContent;
        onEdit(element.id, markdown);
        setIsEditing(false);
    }, [element, editContent, onEdit]);
    /**
     * Обработчик отмены редактирования
     */
    const handleCancel = useCallback(() => {
        if (!element)
            return;
        setEditContent(element.description || '');
        setIsEditing(false);
    }, [element]);
    // Сброс состояния при открытии попапа
    useEffect(() => {
        if (element && isOpen) {
            setEditContent(element.description || '');
            setIsEditing(false);
            setEditorReady(false);
        }
    }, [element, isOpen]);
    // Отслеживание готовности редактора и установка фокуса
    useEffect(() => {
        if (!isEditing) {
            setEditorReady(false);
            return;
        }
        let rafId = null;
        const checkEditorReady = () => {
            var _a;
            const editor = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getEditor();
            if (editor) {
                setEditorReady(true);
                // Устанавливаем фокус на редактор и перемещаем каретку в конец текста
                setTimeout(() => {
                    var _a;
                    const editorInstance = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getEditor();
                    if (editorInstance) {
                        // Перемещаем курсор в конец документа
                        const docSize = editorInstance.state.doc.content.size;
                        editorInstance.commands.setTextSelection(docSize);
                        editorInstance.commands.focus();
                    }
                }, 50);
            }
            else {
                rafId = requestAnimationFrame(checkEditorReady);
            }
        };
        rafId = requestAnimationFrame(checkEditorReady);
        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            setEditorReady(false);
        };
    }, [isEditing]);
    // Обработка кликов вне попапа
    useEffect(() => {
        if (!isOpen)
            return;
        const handleClickOutside = (e) => {
            const target = e.target;
            // Игнорируем клики на дропдауны и тулбары редакторов
            if (target.closest('.mdxeditor-popup-container') ||
                target.closest('.tiptap-toolbar') ||
                target.closest('.floating-toolbar')) {
                return;
            }
            if (popupRef.current && !popupRef.current.contains(target)) {
                if (isEditing) {
                    handleSave();
                }
                else {
                    onClose();
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, isEditing, handleSave]);
    // Вычисление позиции попапа
    const popupPosition = useMemo(() => {
        var _a, _b;
        if (!element || !isOpen)
            return { x: 0, y: 0 };
        const elementX = (((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0) * zoom + stagePosition.x;
        const elementY = (((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0) * zoom + stagePosition.y;
        return calculatePopupPosition(elementX, elementY);
    }, [element, isOpen, zoom, stagePosition]);
    if (!element || !isOpen) {
        return null;
    }
    const handleContentClick = (e) => {
        e.stopPropagation();
        if (!isEditing) {
            setIsEditing(true);
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onRequestClose: onClose, contentLabel: "Element description", className: "element-description-popup", style: {
            overlay: {
                backgroundColor: 'transparent',
                zIndex: 1000,
                position: 'fixed',
            },
            content: {
                position: 'absolute',
                left: `${popupPosition.x}px`,
                top: `${popupPosition.y}px`,
                width: `${POPUP_CONSTANTS.WIDTH}px`,
                maxWidth: '90vw',
                maxHeight: '80vh',
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04)',
                border: '1px solid #e2e8f0',
                outline: 'none',
                overflow: 'hidden',
            },
        }, children: _jsx("div", { ref: popupRef, onClick: handleContentClick, className: isEditing ? 'popup-edit-mode' : 'popup-content', style: { cursor: isEditing ? 'text' : 'pointer' }, children: isEditing ? (_jsxs("div", { className: "popup-edit-mode", children: [_jsxs("div", { className: "editor-wrapper", children: [_jsx(MemoizedEditorCore, { ref: editorRef, content: editContent, onUpdate: handleMarkdownChange, editable: true, placeholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043E\u043F\u0438\u0441\u0430\u043D\u0438\u0435...", style: {
                                    minHeight: '200px',
                                }, className: "tiptap-description-editor" }), editorReady && editorRef.current && (_jsx(TiptapFloatingToolbar, { editor: editorRef.current.getEditor(), zoom: 1, stagePosition: { x: 0, y: 0 }, enabledButtons: ['heading', 'bold', 'italic', 'bulletList'] }))] }), _jsxs("div", { className: "editor-actions", children: [_jsx("button", { onClick: (e) => {
                                    e.stopPropagation();
                                    handleCancel();
                                }, style: {
                                    padding: '8px 16px',
                                    background: 'transparent',
                                    color: '#718096',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.background = '#f7fafc';
                                    e.currentTarget.style.borderColor = '#cbd5e0';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                }, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx("button", { onClick: (e) => {
                                    e.stopPropagation();
                                    handleSave();
                                }, style: {
                                    padding: '8px 16px',
                                    background: '#4299e1',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 4px rgba(66, 153, 225, 0.2)',
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.background = '#3182ce';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(66, 153, 225, 0.3)';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.background = '#4299e1';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(66, 153, 225, 0.2)';
                                }, children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] })) : (_jsx("div", { className: "popup-view-mode markdown-content", children: element.description ? (_jsx(ReactMarkdown, { children: element.description })) : (_jsx("p", { className: "empty-description", children: "\u041D\u0435\u0442 \u043E\u043F\u0438\u0441\u0430\u043D\u0438\u044F. \u041D\u0430\u0436\u043C\u0438\u0442\u0435, \u0447\u0442\u043E\u0431\u044B \u0434\u043E\u0431\u0430\u0432\u0438\u0442\u044C." })) })) }) }));
};
export default ElementDescriptionPopup;
